
import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { popularLocations, otherLocations, resourceItems, companyItems } from "./navData";

interface NavLinksProps {
  locationDropdownOpen: boolean;
  setLocationDropdownOpen: (open: boolean) => void;
  resourcesDropdownOpen: boolean;
  setResourcesDropdownOpen: (open: boolean) => void;
}

export default function NavLinks({
  locationDropdownOpen,
  setLocationDropdownOpen,
  resourcesDropdownOpen,
  setResourcesDropdownOpen
}: NavLinksProps) {
  return <div className="flex items-center space-x-8">
      {/* Nursing Homes Dropdown */}
      <DropdownMenu open={locationDropdownOpen} onOpenChange={setLocationDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <button className="group flex items-center space-x-1.5 text-gray-700 hover:text-grandpal-green-dark transition-colors">
            <span className="font-medium">Nursing Homes</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:text-grandpal-green-dark" style={{
            transform: locationDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
          }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-screen p-0 bg-white shadow-md border-none rounded-none border-t border-gray-100 mt-3.5 z-50" align="start" sideOffset={0}>
          <div className="flex justify-between container py-8 animate-fade-in">
            <div className="flex-1 min-w-[300px]">
              <h3 className="text-grandpal-green-dark mb-6 font-bold text-xl">Popular Nursing Home Locations</h3>
              <div className="grid grid-cols-2 gap-x-16 gap-y-4">
                {popularLocations.map(location => <Link key={location.title} to={location.path} className="text-gray-700 hover:text-grandpal-green-dark transition-colors text-base">
                    {location.title}
                  </Link>)}
                {otherLocations.map(location => <Link key={location.title} to={location.path} className="text-gray-700 hover:text-grandpal-green-dark transition-colors text-base">
                    {location.title}
                  </Link>)}
              </div>
              <div className="mt-6">
                <Link to="/nursing-homes" className="flex items-center text-grandpal-green-dark font-medium hover:text-grandpal-green-darkHover text-base group">
                  View all Nursing Homes
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
            {/* Waitlist section */}
            <div className="flex-1 ml-12 border-l border-gray-100 pl-12">
              <h3 className="text-grandpal-green-dark mb-5 font-lora text-xl font-bold">Join the waitlist for free</h3>
              <div className="flex gap-8 items-start">
                <div className="flex-1">
                  <p className="text-gray-700 mb-5 max-w-md leading-relaxed">
                    Sign up to a waitlist to boost your odds of finding the right nursing home quicker. When there is a match on location and needs, we make a personal introduction.
                  </p>
                  <Link to="/waitlist" className="inline-flex items-center px-4 py-2.5 rounded-lg bg-grandpal-green-light text-grandpal-green-dark font-medium hover:bg-grandpal-green-light/80 transition-all shadow-sm hover:shadow group">
                    Join a Nursing Home Waitlist
                    <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="flex-none">
                  <img src="/lovable-uploads/b4d6b12f-537c-4411-8f7c-9770afaa4de2.png" alt="Coffee and cookies" className="h-32 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Resources Dropdown */}
      <DropdownMenu open={resourcesDropdownOpen} onOpenChange={setResourcesDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <button className="group flex items-center space-x-1.5 text-gray-700 hover:text-grandpal-green-dark transition-colors">
            <span className="font-medium">Resources</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:text-grandpal-green-dark" style={{
            transform: resourcesDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
          }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-screen p-0 bg-white shadow-md border-none rounded-none border-t border-gray-100 mt-3.5 z-50" align="start" sideOffset={0}>
          <div className="container mx-auto py-10 animate-fade-in">
            <div className="grid grid-cols-2 gap-12">
              <div>
                <h3 className="text-grandpal-green-dark mb-6 text-xl font-bold font-lora">Resources</h3>
                <ul className="flex flex-col gap-2">
                  {resourceItems.map(item => <li key={item.title}>
                      <Link to={item.path} className="flex items-start gap-4 hover:bg-grandpal-green-light p-3 rounded-md transition-all group">
                        <span className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-md bg-[#e5f4ee] group-hover:bg-grandpal-green-lighter transition-colors">
                          <span className="text-grandpal-green-dark text-2xl">{item.icon}</span>
                        </span>
                        <div>
                          <div className="font-medium text-gray-800 text-lg mb-0.5">{item.title}</div>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      </Link>
                    </li>)}
                </ul>
              </div>
              <div>
                <h3 className="text-grandpal-green-dark mb-6 text-xl font-bold font-lora">Company</h3>
                <ul className="flex flex-col gap-2">
                  {companyItems.map(item => <li key={item.title}>
                      <Link to={item.path} className="flex items-start gap-4 hover:bg-grandpal-green-light p-3 rounded-md transition-all group">
                        <span className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-md bg-[#e5f4ee] group-hover:bg-grandpal-green-lighter transition-colors">
                          <span className="text-grandpal-green-dark text-2xl">{item.icon}</span>
                        </span>
                        <div>
                          <div className="font-medium text-gray-800 text-lg mb-0.5">{item.title}</div>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Guide Link - Updated to Guides */}
      <Link to="/guides" className="text-gray-700 hover:text-grandpal-green-dark font-medium transition-colors">
        Guides
      </Link>
    </div>;
}
